import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | valid | invalid
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    api.get(`/auth/reset-token/${token}`)
      .then((r) => { setInfo(r.data); setStatus("valid"); })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    setSaving(true);
    try {
      await api.post("/auth/set-password", { token, password });
      toast.success("Password set — you can now sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const isReset = info?.purpose === "reset";

  return (
    <div className="flex min-h-screen bg-[#F4F6F9]">
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#0B1729] p-12 lg:flex">
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #FFB000 0, transparent 45%), radial-gradient(circle at 80% 70%, #4A78A8 0, transparent 40%)" }}
        />
        <div className="relative z-10"><Logo variant="dark" /></div>
        <div className="relative z-10 max-w-md">
          <div className="label-eyebrow mb-4 text-[#FFC24D]">Executive Business Operating System</div>
          <h1 className="font-display text-4xl font-extrabold leading-tight text-white">
            {isReset ? "Choose a new password." : "Welcome aboard."}
          </h1>
          <p className="mt-5 text-base leading-relaxed text-white/60">
            Set a secure password to access your business health, plans, actions and coaching in Amden Compass.
          </p>
        </div>
        <div className="relative z-10 text-xs text-white/30">© {new Date().getFullYear()} Amden Group</div>
      </div>

      <div className="flex w-full items-center justify-center px-6 py-12 lg:w-1/2">
        <div className="w-full max-w-md" data-testid="set-password-page">
          <div className="mb-8 lg:hidden"><Logo variant="light" /></div>

          {status === "checking" && (
            <div className="flex items-center gap-3 text-[#1E2D44]/60"><Icons.Loader2 className="animate-spin" size={18} /> Checking your link…</div>
          )}

          {status === "invalid" && (
            <div data-testid="set-password-invalid">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#C95555]/10 text-[#C95555]"><Icons.Unlink size={22} /></div>
              <h2 className="font-display text-3xl font-bold text-[#0B1729]">Link expired or invalid</h2>
              <p className="mt-2 text-sm text-[#1E2D44]/60">
                This set-password link has expired or has already been used. Ask your Amden administrator to resend
                your invite, or request a new reset link from the sign-in page.
              </p>
              <Button onClick={() => navigate("/login")} className="mt-6 h-11 bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="back-to-login">
                Back to sign in
              </Button>
            </div>
          )}

          {status === "valid" && (
            <>
              <div className="label-eyebrow mb-2">{isReset ? "Reset password" : "Set your password"}</div>
              <h2 className="font-display text-3xl font-bold text-[#0B1729]">
                {info?.name ? `Hi ${info.name.split(" ")[0]}` : "Create your password"}
              </h2>
              <p className="mt-2 text-sm text-[#1E2D44]/60">
                {isReset ? "Enter a new password for " : "Create a password to activate "}
                <span className="font-medium text-[#0B1729]">{info?.email}</span>.
              </p>

              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <Label htmlFor="pw" className="text-[#1E2D44]">New password</Label>
                  <Input id="pw" type="password" data-testid="new-password" value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters"
                    className="mt-1.5 h-11 bg-white" required />
                </div>
                <div>
                  <Label htmlFor="pw2" className="text-[#1E2D44]">Confirm password</Label>
                  <Input id="pw2" type="password" data-testid="confirm-password" value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password"
                    className="mt-1.5 h-11 bg-white" required />
                </div>
                {error && (
                  <div data-testid="set-password-error" className="flex items-center gap-2 rounded-lg bg-[#C95555]/10 px-3 py-2.5 text-sm text-[#C95555]">
                    <Icons.CircleAlert size={16} /> {error}
                  </div>
                )}
                <Button type="submit" disabled={saving} data-testid="set-password-submit"
                  className="h-11 w-full bg-[#FFB000] text-base font-semibold text-[#0B1729] hover:bg-[#E69E00]">
                  {saving ? <Icons.Loader2 className="animate-spin" size={18} /> : (isReset ? "Reset password" : "Set password & continue")}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
